import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, Calendar, Clock, User } from "@phosphor-icons/react";
import PickWhenStep from "@/components/consult/PickWhenStep";
import YourDetailsStep from "@/components/consult/YourDetailsStep";
import { useAuth } from "@/lib/auth";
import { useAuthModal } from "@/components/AuthModal";

const ConsultModalCtx = createContext({ open: () => {}, close: () => {} });
export const useConsultModal = () => useContext(ConsultModalCtx);

export const ConsultModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("consult") === "open") {
      params.delete("consult");
      setIsOpen(true);
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  return (
    <ConsultModalCtx.Provider value={{ open, close }}>
      {children}
      <ConsultDialog isOpen={isOpen} close={close} />
    </ConsultModalCtx.Provider>
  );
};

const STEPS = [
  { id: "when", label: "Pick a time", icon: Calendar },
  { id: "you", label: "Your details", icon: User },
];

const ConsultDialog = ({ isOpen, close }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", division: "couch", date: "", time_slot: "", reason: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState(null);
  const { user, loading: authLoading } = useAuth();
  const { open: openAuth } = useAuthModal();

  // Logged-in users never retype their details
  useEffect(() => {
    if (isOpen && user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
        phone: f.phone || user.phone || "",
      }));
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (!isOpen) return;
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, [isOpen]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      const t = setTimeout(() => {
        setStep(0);
        setDone(false);
        setForm({ name: "", email: "", phone: "", division: "couch", date: "", time_slot: "", reason: "" });
      }, 250);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  const canGoNext = Boolean(form.date && form.time_slot);

  const goNext = () => {
    if (!form.date) { toast.error("Please pick a date first"); return; }
    if (!form.time_slot) { toast.error("Please pick a time slot"); return; }
    setStep(1);
  };
  const goBack = () => setStep(0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      await api.post("/consultations", form);
      setDone(true);
      toast.success("Booked! Confirmation on its way.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not book");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && close()}>
      <DialogContent className="md-consult-modal" data-testid="consult-modal">
        {!authLoading && !user ? (
          <div className="md-consult-success" data-testid="consult-modal-login-gate">
            <DialogTitle className="md-consult-title">Please log in to book your free consultation.</DialogTitle>
            <DialogDescription className="md-consult-sub">
              Signing in takes seconds, your details are filled in automatically.
            </DialogDescription>
            <button
              onClick={() => { close(); openAuth("login"); }}
              className="btn-primary mt-6"
              data-testid="consult-modal-login-btn"
            >
              Log in / Sign up
            </button>
          </div>
        ) : done ? (
          <div className="md-consult-success" data-testid="consult-success">
            <CheckCircle size={64} weight="duotone" style={{ color: "var(--md-primary)" }} />
            <DialogTitle className="md-consult-title">You&apos;re on Shreeja&apos;s calendar.</DialogTitle>
            <DialogDescription className="md-consult-sub">
              A confirmation will arrive at <strong>{form.email}</strong> shortly.
            </DialogDescription>
            <div className="md-consult-success-meta">
              <span className="md-consult-meta-chip" data-testid="consult-success-date">
                <Calendar size={18} weight="duotone" /> {form.date ? new Date(form.date).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" }) : ""}
              </span>
              <span className="md-consult-meta-chip" data-testid="consult-success-time">
                <Clock size={18} weight="duotone" /> {form.time_slot}
              </span>
            </div>
            <button onClick={close} className="btn-primary mt-7" data-testid="consult-success-close">Close</button>
          </div>
        ) : (
          <>
            <div className="md-consult-header">
              <div className="md-label-eyebrow mb-2">Free 20-min consultation</div>
              <DialogTitle className="md-consult-title">A short, free conversation with Shreeja.</DialogTitle>
              <DialogDescription className="md-consult-sub">
                No pressure. We&apos;ll point you to the right path, therapy, mentorship or organisational support.
              </DialogDescription>
            </div>

            {/* Stepper */}
            <ol className="consult-stepper" data-testid="consult-stepper">
              {STEPS.map((s, i) => (
                <li
                  key={s.id}
                  className={`consult-step ${i === step ? "active" : ""} ${i < step ? "done" : ""}`}
                  data-testid={`consult-step-${s.id}`}
                >
                  <span className="consult-step-bubble"><s.icon size={14} weight="bold" /></span>
                  <span className="consult-step-label">{s.label}</span>
                  {i < STEPS.length - 1 && <span className="consult-step-divider" />}
                </li>
              ))}
            </ol>

            {step === 0 && (
              <PickWhenStep
                settings={settings}
                form={form}
                setForm={setForm}
                onNext={goNext}
                onCancel={close}
                canGoNext={canGoNext}
              />
            )}

            {step === 1 && (
              <YourDetailsStep
                form={form}
                setForm={setForm}
                onBack={goBack}
                onSubmit={submit}
                busy={busy}
                lockIdentity={Boolean(user)}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
